/**
 * Password Validation Configuration
 *
 * IMPORTANT: This must stay in sync with backend/middleware/auth.js
 * Both frontend and backend use the same validation rules.
 *
 * Backend location: backend/middleware/auth.js (PASSWORD_REGEX, PASSWORD_SPECIAL_CHARS)
 */

/**
 * Minimum password length
 */
export const PASSWORD_MIN_LENGTH = 8;

/**
 * Password validation requirements
 * Each key corresponds to a specific check with its regex
 */
export const PASSWORD_REQUIREMENTS = {
  minLength: PASSWORD_MIN_LENGTH,
  hasUppercase: /[A-Z]/,
  hasLowercase: /[a-z]/,
  hasNumber: /\d/,
  // Special characters: !@#$%^&*()_+-=[]{};':"|,.<>/?
  hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/,
};

/**
 * Check password against all requirements
 * @param {string} password - Password to validate
 * @returns {Object} Object with boolean for each requirement
 */
export function checkPasswordRequirements(password) {
  return {
    minLength: password.length >= PASSWORD_REQUIREMENTS.minLength,
    hasUppercase: PASSWORD_REQUIREMENTS.hasUppercase.test(password),
    hasLowercase: PASSWORD_REQUIREMENTS.hasLowercase.test(password),
    hasNumber: PASSWORD_REQUIREMENTS.hasNumber.test(password),
    hasSpecial: PASSWORD_REQUIREMENTS.hasSpecial.test(password),
  };
}

/**
 * Check if password meets all requirements
 * @param {string} password - Password to validate
 * @returns {boolean} True if all requirements are met
 */
export function isPasswordValid(password) {
  const checks = checkPasswordRequirements(password);
  return Object.values(checks).every(Boolean);
}

/**
 * Human-readable labels for password requirements
 * Used in UI to show what requirements are met/unmet
 */
export const PASSWORD_REQUIREMENT_LABELS = {
  minLength: `At least ${PASSWORD_MIN_LENGTH} characters`,
  hasUppercase: "One uppercase letter",
  hasLowercase: "One lowercase letter",
  hasNumber: "One number",
  hasSpecial: "One special character",
};

export default {
  PASSWORD_MIN_LENGTH,
  PASSWORD_REQUIREMENTS,
  PASSWORD_REQUIREMENT_LABELS,
  checkPasswordRequirements,
  isPasswordValid,
};
